import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './Punishments.css';
import './CleaningConsoleConcept.css';

const STATUS_FILTER_OPTIONS = [
  { id: '', name: 'Все статусы' },
  { id: 'pending_approval', name: 'На согласовании' },
  { id: 'processing', name: 'Отправка' },
  { id: 'completed', name: 'Выполнено' },
  { id: 'completed_partial', name: 'Частично' },
  { id: 'rejected', name: 'Отклонено' },
  { id: 'relay_failed', name: 'Ошибка отправки' },
];

const statusLabels = {
  pending_approval: 'На согласовании',
  processing: 'Отправка…',
  completed: 'Выполнено',
  completed_partial: 'Выполнено частично',
  rejected: 'Отклонено',
  relay_failed: 'Ошибка отправки',
};

const statusClass = {
  pending_approval: 'punishment-status-pending',
  processing: 'punishment-status-pending',
  completed: 'punishment-status-approved',
  completed_partial: 'punishment-status-pending',
  rejected: 'punishment-status-rejected',
  relay_failed: 'punishment-status-rejected',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${h}:${m}`;
};

function AuthThumb({ filename, className }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let revoke;
    (async () => {
      const token = localStorage.getItem('token');
      if (!token || !filename) return;
      try {
        const res = await fetch(`/api/cleaning/uploads/${encodeURIComponent(filename)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        const u = URL.createObjectURL(blob);
        revoke = () => URL.revokeObjectURL(u);
        if (!cancelled) setUrl(u);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
      if (revoke) revoke();
    };
  }, [filename]);
  return url ? <img src={url} alt="" className={className || ''} /> : <div className="cleaning-thumb-placeholder">…</div>;
}

const CleaningConsoleConcept = () => {
  const [capabilities, setCapabilities] = useState(null);
  const [batches, setBatches] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const [targetIdsText, setTargetIdsText] = useState('');
  const [proofFiles, setProofFiles] = useState([]);
  const [proofPreviewUrls, setProofPreviewUrls] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [expandedId, setExpandedId] = useState(null);
  const [detailItems, setDetailItems] = useState({});
  const [detailLoading, setDetailLoading] = useState(null);

  const [rejectOpenId, setRejectOpenId] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const getToken = () => localStorage.getItem('token');

  const loadCapabilities = useCallback(async () => {
    try {
      const res = await fetch('/api/cleaning/capabilities', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Ошибка');
      setCapabilities(data);
    } catch (e) {
      setError(e.message);
      setCapabilities({ canSubmit: false, canApprove: false });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBatches = useCallback(async () => {
    setListLoading(true);
    setError('');
    try {
      const res = await fetch('/api/cleaning/batches', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Ошибка загрузки');
      setBatches(data.batches || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCapabilities();
  }, [loadCapabilities]);

  useEffect(() => {
    if (capabilities?.canSubmit) loadBatches();
  }, [capabilities?.canSubmit, loadBatches]);

  useEffect(() => {
    const urls = proofFiles.map((f) => URL.createObjectURL(f));
    setProofPreviewUrls(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [proofFiles]);

  const pendingForApproval = useMemo(
    () => batches.filter((b) => b.status === 'pending_approval'),
    [batches]
  );

  const filteredBatches = useMemo(() => {
    if (!statusFilter) return batches;
    return batches.filter((b) => b.status === statusFilter);
  }, [batches, statusFilter]);

  const fixed = capabilities?.fixed;

  const addProofFiles = (fileList) => {
    const next = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    setProofFiles((prev) => [...prev, ...next].slice(0, 5));
  };

  const onProofPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const f = items[i].getAsFile();
        if (f && f.type.startsWith('image/')) files.push(f);
      }
    }
    if (files.length) {
      e.preventDefault();
      addProofFiles(files);
    }
  };

  const submitBatch = async () => {
    setError('');
    setOk('');
    setSubmitLoading(true);
    try {
      const fd = new FormData();
      fd.append('target_ids', targetIdsText);
      proofFiles.forEach((f) => fd.append('proof', f));
      const res = await fetch('/api/cleaning/batches', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не удалось создать пакет');
      setOk(`Пакет #${data.batch?.id} отправлен на согласование.`);
      setTargetIdsText('');
      setProofFiles([]);
      loadBatches();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitLoading(false);
    }
  };

  const toggleExpand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (detailItems[id]) return;
    setDetailLoading(id);
    try {
      const res = await fetch(`/api/cleaning/batches/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Ошибка');
      setDetailItems((prev) => ({ ...prev, [id]: data.items || [] }));
    } catch (e) {
      setError(e.message);
    } finally {
      setDetailLoading(null);
    }
  };

  const approve = async (id) => {
    setActionLoading(`approve-${id}`);
    setError('');
    try {
      const res = await fetch(`/api/cleaning/batches/${id}/approve`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Ошибка');
      setOk('Пакет подтверждён, отправка в Discord выполнена.');
      setRejectOpenId(null);
      loadBatches();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(null);
    }
  };

  const reject = async (id) => {
    setActionLoading(`reject-${id}`);
    setError('');
    try {
      const res = await fetch(`/api/cleaning/batches/${id}/reject`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ note: rejectNote }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Ошибка');
      setOk('Пакет отклонён.');
      setRejectOpenId(null);
      setRejectNote('');
      loadBatches();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="punishments-container">
        <div className="punishments-placeholder">
          <p>Загрузка…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="punishments-container cleaning-page-root">
      <div className="punishments-content cleaning-page-intro">
        <h1 className="cleaning-page-title">Cleaning — выдача наказаний</h1>
        <p className="cleaning-page-sub">
          Только пакетами: после отправки пакета сотрудник с ролью согласующего подтверждает выдачу — затем self отправляет команды в канал{' '}
          <code className="cleaning-code">PUNISHMENT_SELF_RELAY_CHANNEL_ID</code>. Лента «Наказания» не используется.
        </p>
      </div>

      {error && <div className="cleaning-alert cleaning-alert-error">{error}</div>}
      {ok && <div className="cleaning-alert cleaning-alert-success">{ok}</div>}

      {!capabilities?.canSubmit && (
        <div className="punishments-placeholder">
          <p>Доступ только для модераторов из списка staff с привязанным Discord.</p>
        </div>
      )}

      {capabilities?.canSubmit && (
        <>
          <section className="punishments-cards-container cleaning-section">
            <h2 className="cleaning-section-title">Выдача наказаний</h2>
            <p className="cleaning-section-hint">
              Укажите Discord ID нарушителей (по одному в строке или через запятую) и прикрепите скриншот(ы). Параметры наказания зафиксированы системой.
            </p>

            {fixed && (
              <div className="cleaning-fixed-box">
                <div className="cleaning-fixed-row">
                  <span>Команда</span>
                  <strong>{fixed.command}</strong>
                </div>
                <div className="cleaning-fixed-row">
                  <span>Срок</span>
                  <strong>{fixed.duration}</strong>
                  <span className="cleaning-fixed-note">(9999 дней)</span>
                </div>
                <div className="cleaning-fixed-row">
                  <span>Причина</span>
                  <strong>{fixed.reason}</strong>
                </div>
                <div className="cleaning-fixed-row">
                  <span>Сервер</span>
                  <strong>{fixed.server}</strong>
                </div>
              </div>
            )}

            <label className="cleaning-label-block">
              ID нарушителей
              <textarea
                className="cleaning-textarea-lg"
                rows={6}
                placeholder={'123456789012345678\n987654321098765432'}
                value={targetIdsText}
                onChange={(e) => setTargetIdsText(e.target.value)}
                autoComplete="off"
              />
            </label>

            <div className="cleaning-drop" onPaste={onProofPaste} tabIndex={0} role="region" aria-label="Скриншоты">
              <span>
                Скриншоты нарушения — файл или <strong>Ctrl+V</strong> (до 5).
              </span>
              <input
                type="file"
                accept="image/*"
                multiple
                className="cleaning-file-input"
                id="cleaning-proof-batch"
                onChange={(e) => {
                  addProofFiles(e.target.files);
                  e.target.value = '';
                }}
              />
              <label htmlFor="cleaning-proof-batch" className="cleaning-file-btn">
                Выбрать
              </label>
            </div>

            {proofPreviewUrls.length > 0 && (
              <div className="cleaning-previews">
                {proofFiles.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="cleaning-preview-item">
                    <img src={proofPreviewUrls[i]} alt="" className="cleaning-preview-img" />
                    <button
                      type="button"
                      className="cleaning-preview-remove"
                      onClick={() => setProofFiles((prev) => prev.filter((_, j) => j !== i))}
                    >
                      Убрать
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="cleaning-form-actions">
              <button type="button" className="cleaning-submit-btn" disabled={submitLoading} onClick={submitBatch}>
                {submitLoading ? 'Отправка…' : 'Отправить пакет на согласование'}
              </button>
            </div>
          </section>

          {capabilities?.canApprove && pendingForApproval.length > 0 && (
            <section className="punishments-cards-container cleaning-section">
              <h2 className="cleaning-section-title">На согласовании</h2>
              <p className="cleaning-section-hint">Подтвердите или отклоните пакет после проверки скриншотов.</p>
              <div className="cleaning-batch-list">
                {pendingForApproval.map((b) => (
                  <div key={b.id} className="punishment-card cleaning-batch-card">
                    <div className="cleaning-batch-head">
                      <span className="punishment-status-badge punishment-status-pending">На согласовании</span>
                      <span className="cleaning-batch-id">#{b.id}</span>
                    </div>
                    <p className="cleaning-batch-meta">
                      {formatDate(b.created_at)} · от {b.creator_nickname || b.created_by_discord_id} ·{' '}
                      <strong>{b.items_count ?? 0}</strong> ID
                    </p>
                    <div className="cleaning-proof-strip">
                      {(Array.isArray(b.proof_filenames) ? b.proof_filenames : []).map((fn) => (
                        <AuthThumb key={fn} filename={fn} className="cleaning-thumb-img" />
                      ))}
                    </div>
                    <div className="cleaning-batch-actions">
                      <button
                        type="button"
                        className="cleaning-btn-approve"
                        disabled={!!actionLoading}
                        onClick={() => approve(b.id)}
                      >
                        {actionLoading === `approve-${b.id}` ? '…' : 'Подтвердить'}
                      </button>
                      <button
                        type="button"
                        className="cleaning-btn-reject"
                        disabled={!!actionLoading}
                        onClick={() => {
                          if (rejectOpenId === b.id) setRejectOpenId(null);
                          else {
                            setRejectNote('');
                            setRejectOpenId(b.id);
                          }
                        }}
                      >
                        Отклонить
                      </button>
                    </div>
                    {rejectOpenId === b.id && (
                      <div className="cleaning-reject-box">
                        <textarea
                          className="cleaning-textarea-sm"
                          rows={2}
                          placeholder="Комментарий (необязательно)"
                          value={rejectNote}
                          onChange={(e) => setRejectNote(e.target.value)}
                        />
                        <button
                          type="button"
                          className="cleaning-btn-reject-solid"
                          disabled={!!actionLoading}
                          onClick={() => reject(b.id)}
                        >
                          {actionLoading === `reject-${b.id}` ? '…' : 'Отклонить пакет'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="punishments-cards-container cleaning-section">
            <div className="punishments-toolbar cleaning-history-toolbar">
              <h2 className="cleaning-section-title cleaning-section-title-inline">История выдач</h2>
              <select
                className="punishments-search cleaning-filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ maxWidth: 220 }}
              >
                {STATUS_FILTER_OPTIONS.map((o) => (
                  <option key={o.id || 'all'} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
              <button type="button" className="cleaning-refresh-btn" disabled={listLoading} onClick={() => loadBatches()}>
                {listLoading ? '…' : 'Обновить'}
              </button>
            </div>

            {listLoading && batches.length === 0 ? (
              <p className="cleaning-muted">Загрузка…</p>
            ) : filteredBatches.length === 0 ? (
              <div className="punishments-placeholder">
                <p>{batches.length === 0 ? 'Пока нет пакетов.' : 'Нет пакетов с выбранным статусом.'}</p>
              </div>
            ) : (
              <div className="cleaning-batch-list">
                {filteredBatches.map((b) => {
                  const st = b.status || '';
                  const badgeClass = statusClass[st] || 'punishment-status-pending';
                  const label = statusLabels[st] || st;
                  const proofs = Array.isArray(b.proof_filenames) ? b.proof_filenames : [];
                  return (
                    <div key={b.id} className="punishment-card cleaning-batch-card">
                      <div className="cleaning-batch-head">
                        <span className={`punishment-status-badge ${badgeClass}`}>{label}</span>
                        <span className="cleaning-batch-id">#{b.id}</span>
                      </div>
                      <p className="cleaning-batch-meta">
                        {formatDate(b.created_at)} · от {b.creator_nickname || b.created_by_discord_id}
                        {b.items_count != null && (
                          <>
                            {' '}
                            · <strong>{b.items_count}</strong> ID
                            {b.items_ok_count != null && b.status === 'completed_partial' && (
                              <> · отправлено: {b.items_ok_count}</>
                            )}
                          </>
                        )}
                      </p>
                      {b.approved_at && (
                        <p className="cleaning-batch-sub">
                          Подтвердил: {b.approved_by_nickname || b.approved_by_discord_id} · {formatDate(b.approved_at)}
                        </p>
                      )}
                      {b.rejected_at && (
                        <p className="cleaning-batch-sub">
                          Отклонил: {b.rejected_by_nickname || b.rejected_by_discord_id} · {formatDate(b.rejected_at)}
                          {b.rejection_note ? ` — ${b.rejection_note}` : ''}
                        </p>
                      )}
                      {b.relay_error && (
                        <p className="cleaning-batch-err">{b.relay_error}</p>
                      )}
                      {proofs.length > 0 && (
                        <div className="cleaning-proof-strip">
                          {proofs.map((fn) => (
                            <AuthThumb key={fn} filename={fn} className="cleaning-thumb-img" />
                          ))}
                        </div>
                      )}
                      <button type="button" className="cleaning-expand-btn" onClick={() => toggleExpand(b.id)}>
                        {expandedId === b.id ? 'Свернуть ID' : 'Показать все ID в пакете'}
                      </button>
                      {expandedId === b.id && (
                        <div className="cleaning-items-block">
                          {detailLoading === b.id && <p className="cleaning-muted">Загрузка…</p>}
                          {detailItems[b.id] && (
                            <ul className="cleaning-items-list">
                              {detailItems[b.id].map((it) => (
                                <li key={it.id}>
                                  <code>{it.target_discord_id}</code>
                                  <span className={`cleaning-item-st cleaning-item-st-${it.relay_status || 'pending'}`}>
                                    {it.relay_status === 'ok'
                                      ? 'отправлено'
                                      : it.relay_status === 'failed'
                                        ? it.error_message || 'ошибка'
                                        : it.relay_status || '—'}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}

                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default CleaningConsoleConcept;
